import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { UrlSignature } from '../common/apiEndPoint';

interface OtpResponse {
  token?: string;
  email?: string;
  [key: string]: any;
}

interface LoginResponse {
  token?: string;
  email?: string;
  userEmail?: string;
  [key: string]: any;
}

interface TokenPayload {
  exp: number;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class AuthorizationService {
  private otpResponse: OtpResponse | null = null;
  private userEmail = '';

  private readonly STORAGE_KEYS = {
    OTP_RESPONSE: 'otpResponse',
    LOGIN_RESPONSE: 'loginResponse',
    TOKEN: 'token',
    USER_MAIL: 'usermail',
    USER_EMAIL: 'user_email',
  } as const;

  private readonly BACKUP_KEYS = {
    TOKEN: 'backup_token',
    OTP_RESPONSE: 'backup_otpResponse',
    LOGIN_RESPONSE: 'backup_loginResponse',
    TOKEN_TYPE: 'backup_tokenType',
    USER_NAME: 'backup_userName',
    USER_PHONE: 'backup_userPhone',
    USER_MAIL: 'backup_usermail',
  } as const;

  private readonly AUTH_KEYS = ['tokenType', 'userName', 'userPhone', 'usermail'] as const;

  constructor(private http: HttpClient, private urlSignature: UrlSignature) {
    this.initializeFromStorage();
  }

  private initializeFromStorage(): void {
    this.loadOtpResponseFromStorage();
    this.initializeUserEmail();
  }

  private loadOtpResponseFromStorage(): void {
    const storedOtpResponse = localStorage.getItem(this.STORAGE_KEYS.OTP_RESPONSE);
    if (storedOtpResponse) {
      try {
        this.otpResponse = JSON.parse(storedOtpResponse);
      } catch (error) {
        console.error('Error parsing stored OTP response:', error);
        localStorage.removeItem(this.STORAGE_KEYS.OTP_RESPONSE);
      }
    }
  }

  private initializeUserEmail(): void {
    const email = this.getEmailFromStorage();

    if (email) {
      this.userEmail = email;
      return;
    }

    // Fallback to OTP response if available
    if (this.otpResponse?.email && !this.userEmail) {
      this.userEmail = this.otpResponse.email;
    }
  }

  private getEmailFromStorage(): string {
    const storageLocations = [
      () => localStorage.getItem(this.STORAGE_KEYS.USER_MAIL),
      () => localStorage.getItem(this.STORAGE_KEYS.USER_EMAIL),
      () => sessionStorage.getItem(this.STORAGE_KEYS.USER_EMAIL),
      () => sessionStorage.getItem(this.BACKUP_KEYS.USER_MAIL),
    ];

    for (const getEmail of storageLocations) {
      const email = getEmail();
      if (email) {
        return email;
      }
    }

    return '';
  }

  setOtpResponse(response: OtpResponse): void {
    if (!response) {
      console.warn('Invalid OTP response provided');
      return;
    }

    this.otpResponse = response;
    localStorage.setItem(this.STORAGE_KEYS.OTP_RESPONSE, JSON.stringify(response));

    if (response.email) {
      this.setUserEmail(response.email);
    }
  }

  getOtpResponse(): OtpResponse | null {
    return this.otpResponse;
  }

  setUserEmail(email: string): void {
    if (!email || !this.isValidEmail(email)) {
      console.warn('Invalid email provided:', email);
      return;
    }

    this.userEmail = email;
    localStorage.setItem(this.STORAGE_KEYS.USER_MAIL, email);
    sessionStorage.setItem(this.BACKUP_KEYS.USER_MAIL, email);

    console.log('User email set:', this.maskEmail(email));
  }

  getUserEmail(): string {
    if (this.userEmail) {
      return this.userEmail;
    }

    const emailSources = [
      () => this.getEmailFromOtpResponse(),
      () => this.getEmailFromLocalStorage(),
      () => this.getEmailFromSessionStorage(),
      () => this.getEmailFromLoginResponse(),
    ];

    for (const getEmail of emailSources) {
      const email = getEmail();
      if (email) {
        this.userEmail = email;
        return email;
      }
    }

    console.warn('No user email found in any storage location');
    return '';
  }

  private getEmailFromOtpResponse(): string {
    return this.otpResponse?.email || '';
  }

  private getEmailFromLocalStorage(): string {
    return (
      localStorage.getItem(this.STORAGE_KEYS.USER_MAIL) || localStorage.getItem(this.STORAGE_KEYS.USER_EMAIL) || ''
    );
  }

  private getEmailFromSessionStorage(): string {
    const backupEmail =
      sessionStorage.getItem(this.BACKUP_KEYS.USER_MAIL) || sessionStorage.getItem(this.STORAGE_KEYS.USER_EMAIL);

    if (backupEmail) {
      // Restore to localStorage
      localStorage.setItem(this.STORAGE_KEYS.USER_MAIL, backupEmail);
      return backupEmail;
    }

    return '';
  }

  private getEmailFromLoginResponse(): string {
    const loginResponse = localStorage.getItem(this.STORAGE_KEYS.LOGIN_RESPONSE);
    if (!loginResponse) {
      return '';
    }

    try {
      const parsedResponse: LoginResponse = JSON.parse(loginResponse);
      const email = parsedResponse.email || parsedResponse.userEmail;

      if (email) {
        this.setUserEmail(email);
        return email;
      }
    } catch (error) {
      console.error('Error parsing login response for email:', error);
    }

    return '';
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private maskEmail(email: string): string {
    if (!email || !email.includes('@')) {
      return email;
    }

    const [localPart, domain] = email.split('@');
    const maskedLocal = localPart.length > 1 ? localPart[0] + '*'.repeat(Math.max(localPart.length - 1, 2)) : localPart;

    return `${maskedLocal}@${domain}`;
  }

  clearUserEmail(): void {
    this.userEmail = '';
    localStorage.removeItem(this.STORAGE_KEYS.USER_MAIL);
    localStorage.removeItem(this.STORAGE_KEYS.USER_EMAIL);
    sessionStorage.removeItem(this.STORAGE_KEYS.USER_EMAIL);
    sessionStorage.removeItem(this.BACKUP_KEYS.USER_MAIL);
  }

  getCurrentToken(): string | null {
    const tokenSources = [
      () => this.getTokenFromOtpResponse(),
      () => this.getTokenFromLocalStorage(),
      () => this.getTokenFromLoginResponse(),
    ];

    for (const getToken of tokenSources) {
      const token = getToken();
      if (token) {
        return token;
      }
    }

    return null;
  }

  private getTokenFromOtpResponse(): string | null {
    return this.otpResponse?.token || null;
  }

  private getTokenFromLocalStorage(): string | null {
    return localStorage.getItem(this.STORAGE_KEYS.TOKEN);
  }

  private getTokenFromLoginResponse(): string | null {
    const loginResponse = localStorage.getItem(this.STORAGE_KEYS.LOGIN_RESPONSE);
    if (!loginResponse) {
      return null;
    }

    try {
      const parsedResponse: LoginResponse = JSON.parse(loginResponse);
      return parsedResponse.token || null;
    } catch (error) {
      console.error('Error parsing login response:', error);
      return null;
    }
  }

  isAuthenticated(): boolean {
    const token = this.getCurrentToken();
    if (!token) {
      return false;
    }

    return this.isTokenValid(token);
  }

  private isTokenValid(token: string): boolean {
    try {
      const payload: TokenPayload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp > currentTime;
    } catch (error) {
      console.error('Error checking token validity:', error);
      return false;
    }
  }

  makePayment(): Observable<any> {
    const token = this.getCurrentToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    const headers = this.createAuthHeaders(token);
    return this.http.post<any>(this.urlSignature.makepayment, {}, { headers });
  }

  private createAuthHeaders(token: string): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
  }

  recoverAuthenticationData(): boolean {
    if (this.getCurrentToken()) {
      return false;
    }

    console.log('Token missing, attempting recovery...');
    return this.performRecovery();
  }

  private performRecovery(): boolean {
    let recovered = false;

    const recoveryActions = [
      () => this.recoverToken(),
      () => this.recoverOtpResponse(),
      () => this.recoverLoginResponse(),
      () => this.recoverAuthKeys(),
    ];

    for (const recover of recoveryActions) {
      if (recover()) {
        recovered = true;
      }
    }

    if (recovered) {
      console.log('Authentication data recovered successfully');
    }

    return recovered;
  }

  private recoverToken(): boolean {
    const backupToken = sessionStorage.getItem(this.BACKUP_KEYS.TOKEN);
    if (backupToken) {
      localStorage.setItem(this.STORAGE_KEYS.TOKEN, backupToken);
      return true;
    }
    return false;
  }

  private recoverOtpResponse(): boolean {
    const backupOtpResponse = sessionStorage.getItem(this.BACKUP_KEYS.OTP_RESPONSE);
    if (backupOtpResponse) {
      try {
        localStorage.setItem(this.STORAGE_KEYS.OTP_RESPONSE, backupOtpResponse);
        this.otpResponse = JSON.parse(backupOtpResponse);
        return true;
      } catch (error) {
        console.error('Error parsing backup OTP response:', error);
      }
    }
    return false;
  }

  private recoverLoginResponse(): boolean {
    const backupLoginResponse = sessionStorage.getItem(this.BACKUP_KEYS.LOGIN_RESPONSE);
    if (backupLoginResponse) {
      localStorage.setItem(this.STORAGE_KEYS.LOGIN_RESPONSE, backupLoginResponse);
      return true;
    }
    return false;
  }

  private recoverAuthKeys(): boolean {
    let recovered = false;

    this.AUTH_KEYS.forEach((key) => {
      const backupValue = sessionStorage.getItem(`backup_${key}`);
      if (backupValue) {
        localStorage.setItem(key, backupValue);
        if (key === 'usermail') {
          this.userEmail = backupValue;
        }
        recovered = true;
      }
    });

    return recovered;
  }


verifyPaymentStatus(): Observable<any> {
  const token = this.getCurrentToken();
  if (!token) {
    throw new Error('No authentication token found');
  }
  const headers = this.createAuthHeaders(token);
  return this.http.post<any>(this.urlSignature.paymentverificationstatusurl, {}, { headers });
}


  clearBackupData(): void {
    const allBackupKeys = Object.values(this.BACKUP_KEYS);

    allBackupKeys.forEach((key) => {
      sessionStorage.removeItem(key);
    });

    console.log('Backup authentication data cleared');
  }

  hasBackupData(): boolean {
    const criticalBackupKeys = [this.BACKUP_KEYS.TOKEN, this.BACKUP_KEYS.OTP_RESPONSE, this.BACKUP_KEYS.LOGIN_RESPONSE];

    return criticalBackupKeys.some((key) => sessionStorage.getItem(key) !== null);
  }

  clearAuthData(): void {
    this.clearInstanceData();
    this.clearLocalStorageData();
    this.clearBackupData();
  }

  private clearInstanceData(): void {
    this.otpResponse = null;
    this.clearUserEmail();
  }

  private clearLocalStorageData(): void {
    const keysToRemove = [
      this.STORAGE_KEYS.OTP_RESPONSE,
      this.STORAGE_KEYS.LOGIN_RESPONSE,
      this.STORAGE_KEYS.TOKEN,
      'tokenType',
      'userName',
      'userPhone',
      this.STORAGE_KEYS.USER_MAIL,
    ];

    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
    });
  }
}
